import { apiCall } from "../../api/services";
import { CRM_BASE_URL, CRM_LANGUAGE } from "../../types/Crmconfig";
import { crud } from "../Retails/api/retailApi";
import { CRM_SETUP_GROUPS } from "../Settup/Settupcrm";
import { RETAIL_SETUP_GROUPS } from "../Settup/Settupretail";
import { FO_SETUP_GROUPS } from "../Settup/Settupfo";
import { GUIDES, FAQS } from "../header/Headerpages";
import type { SearchAppId, SearchCtx, SearchHit, SearchProvider, SearchTarget } from "./Searchtypes";

/* ------------------------------------------------------------------ */
/* Shared plumbing                                                      */
/* ------------------------------------------------------------------ */

type Rec = Record<string, unknown>;

const rows = (p: any): Rec[] =>
  Array.isArray(p) ? p : Array.isArray(p?.items) ? p.items : Array.isArray(p?.$values) ? p.$values : Array.isArray(p?.data) ? p.data : [];

const cache = new Map<string, { t: number; v: Promise<any> }>();
/** Keeps a list in memory for a minute so typing does not hammer the services. */
const cached = <T,>(key: string, ttlMs: number, fn: () => Promise<T>): Promise<T> => {
  const hit = cache.get(key);
  if (hit && Date.now() - hit.t < ttlMs) return hit.v as Promise<T>;
  const v = fn();
  cache.set(key, { t: Date.now(), v });
  v.catch(() => cache.delete(key));
  return v;
};
const who = (ctx: SearchCtx) => ctx.token.slice(-12);

const statusText = (status: number): string =>
  status === 401 ? "You are signed out." : status === 403 ? "You don't have access to this." : `Request failed (${status}).`;

const getJson = async (url: string, headers: Record<string, string>): Promise<any> => {
  let res: Response;
  try {
    res = await fetch(url, { method: "GET", headers: { Accept: "application/json", ...headers } });
  } catch {
    throw new Error("Can't reach the service. Check your connection.");
  }
  if (res.status === 204) return [];
  if (!res.ok) throw new Error(statusText(res.status));
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : [];
  } catch {
    return [];
  }
};

const idOf = (r: Rec, i: number, keys: string[]): string => {
  for (const k of keys) if (r[k] !== undefined && r[k] !== null && String(r[k])) return String(r[k]);
  return String(i);
};

/** Runs several sources; only fails when every one of them fails. */
const allSources = async (jobs: Promise<SearchHit[]>[]): Promise<SearchHit[]> => {
  const settled = await Promise.allSettled(jobs);
  const ok = settled.flatMap((s) => (s.status === "fulfilled" ? s.value : []));
  if (ok.length === 0 && settled.length > 0 && settled.every((s) => s.status === "rejected")) {
    throw (settled[0] as PromiseRejectedResult).reason;
  }
  return ok;
};

const bearer = (ctx: SearchCtx) => ({ Authorization: `Bearer ${ctx.token}` });

/* ------------------------------------------------------------------ */
/* Hospitality                                                          */
/* ------------------------------------------------------------------ */

const propertyList = (ctx: SearchCtx, endpoint: string): Promise<Rec[]> =>
  cached(`hms:${who(ctx)}:${endpoint}`, 60_000, async () => {
    const res: any = await apiCall({
      service: "property",
      endpoint,
      method: "GET",
      headers: { ...bearer(ctx), "Content-Type": "application/json" },
    } as any);
    if (res && res.error) throw new Error(typeof res.error === "string" ? res.error : "The request failed");
    return rows(res);
  });

const HMS_SOURCES: { endpoint: string; kind: string; ids: string[] }[] = [
  { endpoint: "Rooms", kind: "Room", ids: ["roomId", "id", "roomNumber"] },
  { endpoint: "RoomType", kind: "Room type", ids: ["roomTypeId", "id"] },
  { endpoint: "RoomCategory", kind: "Room category", ids: ["categoryId", "id"] },
  { endpoint: "Amenity", kind: "Amenity", ids: ["amenityId", "id"] },
  { endpoint: "hotels", kind: "Property", ids: ["hotelId", "id"] },
];

const hospitality: SearchProvider = {
  app: "hospitality",
  label: "Hospitality",
  search: (_q, ctx) =>
    allSources(
      HMS_SOURCES.map(async (s) =>
        (await propertyList(ctx, s.endpoint)).map((r, i) => ({
          id: idOf(r, i, s.ids),
          app: "hospitality" as SearchAppId,
          kind: s.kind,
          record: r,
          target: { tab: "hospitality" },
        }))
      )
    ),
};

/* ------------------------------------------------------------------ */
/* Accounting                                                           */
/* ------------------------------------------------------------------ */

const ACC_BASE = "https://acc.cilpron.com";
const ACC_SOURCES: { path: string; kind: string; ids: string[]; paged: boolean }[] = [
  { path: "/api/dashboard/ledger/accounts", kind: "Ledger account", ids: ["accountCode", "code", "id"], paged: false },
  { path: "/api/receivables/customers", kind: "Customer", ids: ["customerCode", "id"], paged: true },
  { path: "/api/payables/vendors", kind: "Vendor", ids: ["vendorCode", "id"], paged: true },
];

const accounting: SearchProvider = {
  app: "accounting",
  label: "Accounting",
  search: (_q, ctx) =>
    allSources(
      ACC_SOURCES.map(async (s) => {
        const list = await cached(`acc:${who(ctx)}:${s.path}`, 60_000, () =>
          getJson(`${ACC_BASE}${s.path}${s.paged ? "?page=1&pageSize=500" : ""}`, bearer(ctx)).then(rows)
        );
        return list.map((r, i) => ({
          id: idOf(r, i, s.ids),
          app: "accounting" as SearchAppId,
          kind: s.kind,
          record: r,
          target: { tab: "Accounting" },
        }));
      })
    ),
};

/* ------------------------------------------------------------------ */
/* CRM                                                                  */
/* ------------------------------------------------------------------ */

const CRM_SOURCES: { path: string; kind: string }[] = [
  { path: "api/sales/leads", kind: "Lead" },
  { path: "api/customers/contacts", kind: "Contact" },
  { path: "api/customers/accounts", kind: "Account" },
  { path: "api/sales/opportunities", kind: "Opportunity" },
  { path: "api/service/cases", kind: "Case" },
  { path: "api/commerce/quotes", kind: "Quote" },
  { path: "api/commerce/orders", kind: "Order" },
  { path: "api/commerce/invoices", kind: "Invoice" },
  { path: "api/commerce/products", kind: "Product" },
  { path: "api/marketing/campaigns", kind: "Campaign" },
];

const crm: SearchProvider = {
  app: "crm",
  label: "CRM",
  search: (q, ctx) =>
    allSources(
      CRM_SOURCES.map(async (s) => {
        const headers: Record<string, string> = { ...bearer(ctx) };
        if (CRM_LANGUAGE) headers["Accept-Language"] = String(CRM_LANGUAGE);
        const base = String(CRM_BASE_URL).replace(/\/$/, "");
        // The service may or may not honour "search"; the list is also filtered on our side.
        const data = await getJson(`${base}/${s.path}?page=1&pageSize=200&search=${encodeURIComponent(q)}`, headers);
        return rows(data).map((r, i) => ({
          id: idOf(r, i, ["id", "number"]),
          app: "crm" as SearchAppId,
          kind: s.kind,
          record: r,
          target: { tab: "CRM" },
        }));
      })
    ),
};

/* ------------------------------------------------------------------ */
/* Retail & POS                                                         */
/* ------------------------------------------------------------------ */

const RETAIL_SOURCES: { route: string; kind: string }[] = [
  { route: "catalog/products", kind: "Product" },
  { route: "customers/customers", kind: "Customer" },
  { route: "organization/stores", kind: "Store" },
  { route: "procurement/suppliers", kind: "Supplier" },
];

const retail: SearchProvider = {
  app: "retail",
  label: "Retail & POS",
  search: (q) =>
    allSources(
      RETAIL_SOURCES.map(async (s) => {
        const page: any = await (crud<any>(s.route) as any).list({ page: 1, pageSize: 25, search: q });
        return rows(page).map((r, i) => ({
          id: idOf(r, i, ["id", "code", "sku"]),
          app: "retail" as SearchAppId,
          kind: s.kind,
          record: r,
          target: { tab: "Retail" },
          trusted: true,
        }));
      })
    ),
};

/* ------------------------------------------------------------------ */
/* Finance & Operations                                                 */
/* ------------------------------------------------------------------ */

const env = ((import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {}) as Record<string, string | undefined>;
const FO_BASE = (env.VITE_FO_GATEWAY_URL || "https://fo.cilpron.com").replace(/\/+$/, "");
const FO_COMPANY = env.VITE_FO_COMPANY_ID || "11111111-1111-1111-1111-111111111111";
const FO_SOURCES: { path: string; kind: string }[] = [
  { path: "api/masterdata/items", kind: "Item" },
  { path: "api/masterdata/partners", kind: "Business partner" },
  { path: "api/workforce/workers", kind: "Worker" },
  { path: "api/masterdata/locations", kind: "Warehouse" },
  { path: "api/masterdata/companies", kind: "Legal entity" },
];

const finance: SearchProvider = {
  app: "finance",
  label: "Finance & Operations",
  search: (_q, ctx) =>
    allSources(
      FO_SOURCES.map(async (s) => {
        const list = await cached(`fo:${who(ctx)}:${s.path}`, 60_000, () =>
          getJson(`${FO_BASE}/${s.path}?page=1&pageSize=200`, { ...bearer(ctx), "X-Company-Id": FO_COMPANY }).then(rows)
        );
        return list.map((r, i) => ({
          id: idOf(r, i, ["id", "code"]),
          app: "finance" as SearchAppId,
          kind: s.kind,
          record: r,
          target: { tab: "Finance" },
        }));
      })
    ),
};

/* ------------------------------------------------------------------ */
/* Screens, settings and help (no network, answers instantly)           */
/* ------------------------------------------------------------------ */

interface IndexEntry {
  app: SearchAppId;
  kind: string;
  title: string;
  subtitle: string;
  keywords: string;
  target: SearchTarget;
}

const setup = (app: SearchAppId, title: string, value: string, group = "", keywords = ""): IndexEntry => ({
  app,
  kind: "Setup screen",
  title,
  subtitle: group ? `Settings · ${group}` : "Settings",
  keywords,
  target: { tab: "settings", settupValue: value },
});

let indexCache: IndexEntry[] | null = null;
const buildIndex = (): IndexEntry[] => {
  if (indexCache) return indexCache;
  const out: IndexEntry[] = [
    setup("hospitality", "Add Property", "Added Property", "Property"),
    setup("hospitality", "Add Rooms", "Added Room", "Room Setups", "room new"),
    setup("hospitality", "Room Category", "Room Category", "Room Setups"),
    setup("hospitality", "Amenity", "Amenity", "Room Setups", "amenities"),
    setup("hospitality", "Room Types", "RoomTypes", "Room Setups"),
    setup("settings", "Roles", "AccessRoles", "Access Control", "role access"),
    setup("settings", "Permissions", "AccessPermissions", "Access Control"),
    setup("settings", "Assign Permissions", "AccessAssign", "Access Control", "grant role access"),
    setup("accounting", "Chart of accounts", "AccActAccounts", "Accounting Setup", "ledger"),
    setup("accounting", "Account groups", "AccActGroups", "Accounting Setup"),
    setup("accounting", "Bank accounts", "AccActBanks", "Accounting Setup", "bank"),
    setup("accounting", "Tax rates", "AccActTaxes", "Accounting Setup", "vat tax"),
    setup("accounting", "Customers", "AccActCustomers", "Accounting Setup", "receivables"),
    setup("accounting", "Vendors", "AccActVendors", "Accounting Setup", "payables suppliers"),
    setup("accounting", "Asset categories", "AccActAssetCats", "Accounting Setup", "fixed assets"),
  ];
  CRM_SETUP_GROUPS.forEach((g) => g.items.forEach((it) => out.push(setup("crm", it.title, `Crm:${it.key}`, g.title))));
  RETAIL_SETUP_GROUPS.forEach((g) => g.items.forEach((it) => out.push(setup("retail", it.title, `Retail:${it.key}`, g.title))));
  FO_SETUP_GROUPS.forEach((g) => g.items.forEach((it) => out.push(setup("finance", it.title, `Fo:${it.key}`, g.title))));

  const page = (title: string, headerPage: NonNullable<SearchTarget["headerPage"]>, keywords: string): IndexEntry => ({
    app: "settings",
    kind: "Page",
    title,
    subtitle: "Account and help",
    keywords,
    target: { tab: "headerPages", headerPage },
  });
  out.push(
    page("My account", "profile", "profile sign out roles permissions access"),
    page("Company", "company", "organisation subscription apps"),
    page("Notifications", "notifications", "alerts unread"),
    page("All settings", "settings", "preferences"),
    page("Help center", "help", "support questions"),
    page("User guides", "guides", "how to steps"),
    page("Keyboard shortcuts", "shortcuts", "keys"),
    page("Contact support", "contact", "email help")
  );
  GUIDES.forEach((g) =>
    out.push({
      app: "settings",
      kind: "Guide",
      title: g.title,
      subtitle: g.summary,
      keywords: g.steps.join(" "),
      target: { tab: "headerPages", headerPage: "guides" },
    })
  );
  FAQS.forEach((f) =>
    out.push({
      app: "settings",
      kind: "Help",
      title: f.q,
      subtitle: f.topic,
      keywords: f.a,
      target: { tab: "headerPages", headerPage: "help" },
    })
  );
  indexCache = out;
  return out;
};

/** A few screens of one app, for the empty search box. */
export const quickLinks = (app: SearchAppId | null, limit = 5): { title: string; app: SearchAppId; target: SearchTarget }[] => {
  const all = buildIndex().filter((e) => e.kind === "Setup screen" || e.kind === "Page");
  const mine = app ? all.filter((e) => e.app === app) : [];
  return (mine.length ? mine : all.filter((e) => e.kind === "Page")).slice(0, limit);
};

const screens: SearchProvider = {
  app: "settings",
  label: "Settings & help",
  search: async () =>
    buildIndex().map((e, i) => ({
      id: `${e.kind}:${e.title}:${i}`,
      app: e.app,
      kind: e.kind,
      record: { title: e.title, subtitle: e.subtitle, keywords: e.keywords },
      target: e.target,
    })),
};

/* ------------------------------------------------------------------ */
/* Registry                                                             */
/* ------------------------------------------------------------------ */

const providers: SearchProvider[] = [screens, hospitality, accounting, crm, retail, finance];

/**
 * Plug another app in. Return SearchHit[] from search(); the shared code scores,
 * groups, highlights and ranks them (and puts the app first when the user is in it).
 *
 *   registerSearchProvider({
 *     app: "task", label: "Tasks",
 *     search: async (q, ctx) => (await listTasks(ctx.token, q)).map(t => ({
 *       id: t.id, app: "task", kind: "Task", record: t, target: { tab: "Task" },
 *     })),
 *   });
 */
export const registerSearchProvider = (p: SearchProvider): void => {
  const at = providers.findIndex((x) => x.app === p.app && x.label === p.label);
  if (at >= 0) providers[at] = p;
  else providers.push(p);
};

export const getSearchProviders = (): SearchProvider[] => providers;